// ------------------------------------------------------------------
//  RHFTextField  –  placeholder colour synced with RHFCustomSelect
// ------------------------------------------------------------------
import { useRef, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { FormLabel, IconButton, InputAdornment, Stack, TextField, useTheme } from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

const OUTER_LABEL_STYLE: React.CSSProperties = {
  fontSize: 12,
  color: '#718096',
};

type RHFTextFieldProps = {
  name: string;
  type?: 'text' | 'password' | 'email' | 'number' | 'datetime-local' | 'date';
  variant?: 'outlined' | 'filled' | 'standard';
  readOnly?: boolean;
  StartIcon?: React.ReactNode;
  EndIcon?: React.ReactNode;
  outerLabel?: string;
  fullWidth?: boolean;
  maxLength?: number;
} & Omit<React.ComponentProps<typeof TextField>, 'type' | 'variant'>;

export function RHFTextField({
  name,
  type = 'text',
  variant = 'outlined',
  readOnly = false,
  StartIcon,
  EndIcon,
  outerLabel,
  required = false,
  fullWidth = true,
  maxLength,
  ...textFieldProps
}: RHFTextFieldProps): JSX.Element {
  const { control } = useFormContext();
  const theme: any = useTheme();
  const [showPassword, setShowPassword] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const [charCount, setCharCount] = useState(0);

  const handleFocus = (): void => {
    if (
      (type === 'datetime-local' || type === 'date') &&
      inputRef.current
    ) {
      inputRef.current.showPicker();
    }
  };

  const endAdornment =
    type === 'password' ? (
      <InputAdornment position="end">
        <IconButton onClick={() => setShowPassword((v) => !v)}>
          {showPassword ? <Visibility /> : <VisibilityOff />}
        </IconButton>
      </InputAdornment>
    ) : (
      EndIcon
    );

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => (
        <Stack gap="0.6rem">
          {outerLabel && (
            <FormLabel error={false} style={OUTER_LABEL_STYLE}>
              {outerLabel}{' '}
              {required && <span style={{ color: '#DC2626', fontSize: 14 }}>*</span>}
            </FormLabel>
          )}

          {maxLength && (
            <div
              style={{
                textAlign: 'right',
                fontSize: 12,
                color: theme?.palette?.neutral?.[500] || '#888',
              }}
            >
              {charCount}/{maxLength}
            </div>
          )}

          <TextField
            {...field}
            value={field.value ?? ''}
            fullWidth={fullWidth}
            type={showPassword ? 'text' : type}
            variant={variant}
            onChange={(e) => {
              field.onChange(e.target.value);
              if (typeof maxLength === 'number') setCharCount(e.target.value.length);
            }}
            InputLabelProps={{
              sx: {
                backgroundColor: theme?.palette?.background?.paper || '#fff',
                px: '4px',
                color: theme?.palette?.text?.primary || '#000',
              },
            }}
            /*  >>>  SINGLE , CORRECT InputProps  <<<  */
            InputProps={{
              readOnly,
              startAdornment: StartIcon,
              endAdornment,
              inputRef,
              onFocus: handleFocus,
              sx: {
                backgroundColor: theme?.palette?.background?.paper || '#fff',
                borderRadius: '4px',
                transition: 'all 0.2s ease',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme?.palette?.neutral?.[200] || '#e0e0e0',
                },
                '&:hover': {
                  backgroundColor: theme?.palette?.neutral?.[50] || '#fafafa',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme?.palette?.neutral?.[300] || '#bdbdbd',
                },
                '&.Mui-focused': {
                  boxShadow: `0 0 0 3px ${(
                    theme?.palette?.primary?.main || '#1976d2'
                  )
                    .replace('#', '')
                    .slice(0, 6)}30`,
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme?.palette?.primary?.main || '#1976d2',
                },
                '&.Mui-error .MuiOutlinedInput-notchedOutline': {
                  borderColor: `#DC2626 !important`,
                },
                '&.Mui-error.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: `#DC2626 !important `,
                },
                '&.Mui-error:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: `#DC2626 !important`,
                },
                /*  >>>  PLACEHOLDER COLOUR (same as RHFCustomSelect)  <<<  */
                '& .MuiInputBase-input::placeholder': {
                  color: theme?.palette?.neutral?.[600] || '#999',
                  opacity: 1,
                },
                '& .MuiInputBase-input': { fontSize: 12 },
                '& .MuiInputAdornment-root , & .MuiSvgIcon-root': {
                  color: theme?.palette?.neutral?.[500] || '#888',
                },
              },
            }}
            {...textFieldProps}
            error={Boolean(fieldState.error)}
            helperText={fieldState.error?.message}
            FormHelperTextProps={{
              sx: {
                color: '#DC2626 !important',
                ml: 0,
                mr: 0,
                mt: '4px',
                textAlign: 'left',
              },
            }}
          />
        </Stack>
      )}
    />
  );
}